import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import JsonView from 'react18-json-view';
import { Tabs, Button, MessagePlugin } from 'tdesign-react';
import { API_BASE_URL } from '../../../../const/constants.js';

const { TabPanel } = Tabs;

/**
 * 文档解析结果渲染器
 * 通过 BFF 接口下载并解压 ResultUrl 的 zip，返回 markdown 和 JSON 文本内容
 */
const DocParseRenderer = ({ params, request }) => {
  const [loading, setLoading] = useState(false);
  const [mdContent, setMdContent] = useState('');
  const [jsonContent, setJsonContent] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('markdown');

  const resultUrl = params?.ResultUrl;

  useEffect(() => {
    if (!resultUrl) return;
    fetchParseResult(resultUrl);
  }, [resultUrl]);

  const fetchParseResult = async (url) => {
    setLoading(true);
    setError('');
    setMdContent('');
    setJsonContent(null);

    try {
      // 由 BFF 层代理下载 zip 并解压，规避 COS 跨域问题
      const res = await fetch(`${API_BASE_URL}demo-common/multimodalDocParse/parseResult`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ resultUrl: url }),
      });

      const data = await res.json();

      if (data.code !== 0 || !data.data) {
        throw new Error(data.message || '解析结果失败');
      }

      const { markdown, json } = data.data;

      if (markdown) {
        setMdContent(markdown);
      }

      if (json) {
        try {
          setJsonContent(JSON.parse(json));
        } catch {
          setJsonContent(json);
        }
      }

      if (!markdown && !json) {
        setError('结果文件中未找到 markdown 或 json 内容');
      }
    } catch (err) {
      console.error('[DocParse] 解析结果失败:', err);
      setError(err.message || '解析结果失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (content) => {
    if (!content) return;
    const text = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    navigator.clipboard.writeText(text).then(() => {
      MessagePlugin.success('已复制到剪贴板');
    }).catch(() => {
      MessagePlugin.error('复制失败');
    });
  };

  if (!resultUrl) return null;

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>正在解析结果文件...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{ color: '#e34d59', marginBottom: '12px' }}>解析失败: {error}</div>
        <Button variant="outline" size="small" onClick={() => fetchParseResult(resultUrl)}>重试</Button>
        <a
          href={resultUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginLeft: '12px', color: '#4A70FF', fontSize: '14px' }}
        >
          直接下载结果文件
        </a>
      </div>
    );
  }

  // 根据请求的 ResultType 决定显示哪些内容
  // 1=JSON, 2=Markdown, 3=XML, 9=全部
  const resultType = request?.ResultType ?? 9;
  const showMarkdown = mdContent && (resultType === 2 || resultType === 9);
  const showJson = jsonContent && (resultType === 1 || resultType === 9);
  const hasBothTabs = showMarkdown && showJson;

  // 当前展示的内容（用于复制）
  const currentContent = hasBothTabs
    ? (activeTab === 'markdown' ? mdContent : jsonContent)
    : (showMarkdown ? mdContent : jsonContent);

  const renderMarkdown = () => (
    <div className="markdown_container" style={{ padding: '12px' }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeRaw, rehypeKatex]}
      >
        {mdContent}
      </ReactMarkdown>
    </div>
  );

  const renderJson = () => (
    <div style={{ padding: '12px' }}>
      {typeof jsonContent === 'string' ? (
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{jsonContent}</pre>
      ) : (
        <JsonView
          src={jsonContent}
          theme="winter-is-coming"
          displayDataTypes={false}
        />
      )}
    </div>
  );

  return (
    <div className="doc-parse-result">
      {hasBothTabs ? (
        <Tabs value={activeTab} onChange={setActiveTab} style={{ width: '100%' }}>
          <TabPanel value="markdown" label="Markdown">{renderMarkdown()}</TabPanel>
          <TabPanel value="json" label="JSON">{renderJson()}</TabPanel>
        </Tabs>
      ) : (
        <>
          {showMarkdown && renderMarkdown()}
          {showJson && renderJson()}
        </>
      )}

      {/* 底部操作栏：复制 + 下载，左对齐 */}
      <div style={{ display: 'flex', gap: '8px', padding: '12px' }}>
        <Button
          theme="default"
          variant="outline"
          onClick={() => handleCopy(currentContent)}
        >
          复制结果
        </Button>
        <Button
          theme="default"
          variant="outline"
          onClick={() => window.open(resultUrl, '_blank')}
        >
          下载完整结果(zip)
        </Button>
      </div>
    </div>
  );
};

export default DocParseRenderer;
